import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import {
  data,
  SAV_APR,
  SAV_ENC
} from '@lba-dev/package.local-globals/SAVStatus';
import { listMailsSST } from '@lba-dev/package.local-globals/listMails';

import { setDialogContentProp } from '../../actions/dialog';
import store from '../../store';
import api from '../../api';
import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import CustomTextField from '../CustomInputs/CustomTextField';

const mapStateToProps = ({
  dialog: {
    contentProps: {
      savInter,
      status,
      comment,
      date,
      desc,
      consignes,
      sendMailSST,
      mailSSTData,
      showMailPreview,
    }
  },
  connectedUser,
}) => ({
  savInter,
  status,
  comment,
  date,
  desc,
  consignes,
  sendMailSST,
  mailSSTData,
  showMailPreview,
  user: connectedUser,
});

// Statuts Pré-SAV
const PRE_SAV_STATUSES = data.slice(0, 4)
  .map(d => ({ _id: d._id, name: d.name }));

class PriseEnChargeDialogPreSAV extends PureComponent {
  state = {
    loading: false,
    artisan: null,
  }

  async componentDidMount() {
    // Initialiser le statut avec le statut actuel du Pré-SAV
    const { savInter } = this.props;
    const defaultValues = {
      desc: savInter.infoDescSAV?.desc,
      consignes: savInter.infoDescSAV?.consignes,
    };
    if (savInter && savInter.status !== undefined) {
      store.dispatch(setDialogContentProp('status')(savInter.status));
    } else {
      store.dispatch(setDialogContentProp('status')(SAV_APR));
    }

    // Initialiser la date avec la date d'intervention actuelle
    if (
      savInter &&
      savInter.date &&
      (savInter.date.preSavDate || savInter.date.intervention)
    ) {
      store.dispatch(setDialogContentProp('date')(
        new Date(
          savInter.date.preSavDate ?? savInter.date.intervention
        )
      ));
    } else {
      store.dispatch(setDialogContentProp('date')(new Date()));
    }

    // Initialiser les autres champs
    store.dispatch(setDialogContentProp(
      'desc'
    )(defaultValues.desc ?? savInter.infoDesc?.desc));
    store.dispatch(setDialogContentProp(
      'consignes'
    )(defaultValues.consignes ?? savInter.infoDesc?.consignes));

    if (savInter.artisan) {
      try {
        const [artisan] = await api.artisans.getAll({
          query: JSON.stringify({ _id: savInter.artisan }),
        }).then((e) => e.body().map((r) => r.data()));
        if (artisan) {
          this.setState({ artisan });
        }
      } catch (e) {
        // artisan fetch failed silently
      }
    }
  }

  updateData = (prop, value) => {
    store.dispatch(setDialogContentProp(prop)(value));
  }

  buildMailSST = () => {
    const { date, desc, savInter, user } = this.props;
    const { artisan } = this.state;
    if (!artisan) {
      return null;
    }
    const savDesc = desc
      || savInter.infoDescSAV?.desc
      || savInter.infoDesc?.desc || '';
    try {
      return listMailsSST({
        savInter, artisan, date, savDesc, user,
      }).find((e) => e.name === 'ENVOIE DU SAV');
    } catch (e) {
      return null;
    }
  }

  toggleSendMailSST = () => {
    const { sendMailSST } = this.props;
    const newValue = !sendMailSST;
    this.updateData('sendMailSST', newValue);
    if (newValue) {
      const mail = this.buildMailSST();
      if (mail) {
        this.updateData('mailSSTData', {
          subject: mail.subject,
          text: mail.text,
          title: mail.title,
          desMail: mail.desMail,
        });
      }
    }
  }

  toggleMailPreview = () => {
    this.updateData('showMailPreview', !this.props.showMailPreview);
  }

  updateMailField = (field, value) => {
    const { mailSSTData } = this.props;
    this.updateData('mailSSTData', { ...mailSSTData, [field]: value });
  }

  render() {
    const {
      status,
      comment,
      date,
      desc,
      consignes,
      savInter,
      sendMailSST,
      mailSSTData,
      showMailPreview,
    } = this.props;
    const { artisan } = this.state;
    return (
      <Grid container spacing={3} sx={{
        padding: '20px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        {/* Informations Pré-SAV */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations Pré-SAV
              </Typography>
              <Typography variant="body2" color="textSecondary">
                OS: {savInter?.id} | Client: {
                  savInter?.client?.name
                } {savInter?.client?.firstname}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Date d'intervention */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Date d'intervention
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            startDate={date}
            onChange={
              (newDate) => this.updateData(
                'date', newDate ? newDate.toDate() : null
              )
            }
            label="Date"
            before
          />
        </Grid>
        <Grid item xs={6}>
          <TimePicker
            name="date"
            value={date}
            onChange={(e) => {
              const numberPart = e.target.value.split(':');
              const newDate = date ? new Date(date) : new Date();
              newDate.setHours(
                Number.parseInt(numberPart[0], 10),
                Number.parseInt(numberPart[1], 10),
                0,
                0
              );
              this.updateData('date', newDate);
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Description et Consignes */}
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Description"
            texttransform="none"
            multiline
            rows={3}
            fullWidth
            value={desc || ''}
            path="desc"
            setData={(p, v) => this.updateData(p, v)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Consignes"
            texttransform="none"
            multiline
            rows={3}
            fullWidth
            value={consignes || ''}
            path="consignes"
            setData={(p, v) => this.updateData(p, v)}
            inputProps={{ maxLength: 150 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Statut */}
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            disabled
          >
            Statut
          </Button>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={status}
              onChange={
                (e) => this.updateData(
                  'status', Number(e.target.value)
                )
              }
              style={{ display: 'inline' }}
            >
              {PRE_SAV_STATUSES.map((s) => (
                <FormControlLabel
                  key={s._id}
                  value={s._id}
                  control={<Radio />}
                  label={s.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Envoi coordonnées SST */}
        {status === SAV_ENC && artisan && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} style={{
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!sendMailSST}
                    onChange={this.toggleSendMailSST}
                  />
                }
                label="Envoyer les coordonnées au SST"
              />
              {sendMailSST && mailSSTData && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={this.toggleMailPreview}
                >
                  {showMailPreview ? 'Masquer le mail' : 'Voir le mail'}
                </Button>
              )}
            </Grid>
            {sendMailSST && mailSSTData && showMailPreview && (
              <>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Objet du mail"
                    texttransform="none"
                    fullWidth
                    value={mailSSTData.subject || ''}
                    path="subject"
                    setData={
                      (p, v) => this.updateMailField('subject', v)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Corps du mail"
                    texttransform="none"
                    fullWidth
                    rows={10}
                    value={mailSSTData.text || ''}
                    path="text"
                    setData={
                      (p, v) => this.updateMailField('text', v)
                    }
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* Commentaire */}
        <Grid item xs={12}>
          <CustomTextField
            customTimeOut={0}
            grid
            xs={12}
            texttransform="none"
            rows={4}
            label="Commentaire..."
            value={comment || ''}
            path="comment"
            setData={(p, v) => this.updateData(p, v)}
          />
        </Grid>
      </Grid>
    );
  }
}

export default compose(
  connect(mapStateToProps)
)(PriseEnChargeDialogPreSAV);
